/**
 * The three people in the juhlasali who are neither dancing nor in the way:
 * the valvoja in his corner, the orga at the end wall pointing at Kimmo's
 * chair, and Kempf at the sauna door.
 */

import {
  decor,
  handle,
  boxCenter,
  type Box,
  type DecorSpec,
  type HandleSpec,
  type SaunaSpec,
  type SignSpec,
  type SitterSpec,
  type StanderSpec,
} from '../world/Level';
import { COMPUTERS, KIMMO_TABLE_INDEX, LAN_EAST_WALL, LAN_HALL_EXTENSION } from './levelJuhlasali';
import { DJ_SOLIDI, TELTAN_FANIT } from './levelTeltta';

/* ----------------------------------------------- juhlasalin valvoja ------ */

/**
 * The bench at the far end of the hall, and the man sitting on it.
 *
 * It is on the same south wall the baari is on, at the other end of it: the
 * bar is the first thing on your right as you come in, and this is the last.
 * That is not decoration for its own sake - the far right-hand corner is the
 * one part of the juhlasali with nothing in it. The tables are all north of
 * the runway, the finish is behind them, and the corner a runner turns *away*
 * from at the end of the room had been empty since the hall was blocked out.
 *
 * He is the only figure in the venue who is awake. Everybody else is face
 * down in a runway or in a bed, and a hall where every single person is
 * unconscious reads as a hall where nothing is happening; one man sitting up
 * with his eyes open says the finaali is a thing somebody is running, and that
 * the row of screens behind you is being watched by somebody.
 *
 * The same constraint as the bar applies and is the reason both are at the
 * wall. The wall-side lane of the runway is `z = -31.975 .. -30.0`; the bench
 * is 0.42 m deep against a wall at `z = -30.0` and a seated man puts his knees
 * about 0.75 m in front of his hips, so the furthest forward anything here
 * reaches is `z ~ -30.9` and 1.08 m of the lane is left beside him. Everything is decor, cylinders and signs - no solids - so
 * none of it is in the fingerprint and none of it is in anybody's way. See
 * `src/config/CLAUDE.md`.
 */
export const VALVOJA = {
  /** The bench. `wallZ` is the inside face of `navetta-etela-b`, as at the bar. */
  minX: 144.85 + LAN_HALL_EXTENSION,
  maxX: 146.85 + LAN_HALL_EXTENSION,
  frontZ: -30.42,
  wallZ: -30.0,
  seatTop: 0.46,
  slab: 0.06,
  /** Where he sits on it, and where his hips are over the seat. */
  x: 145.75 + LAN_HALL_EXTENSION,
  hipZ: -30.16,
  height: 1.78,
  /**
   * His sign, on the wall over his head. Wide for its height because
   * `createSign` sizes the font off the *height* and does not shrink to fit -
   * eighteen letters need about six and a half times the height to fit in.
   */
  signY: 1.78,
  signWidth: 1.6,
  signHeight: 0.24,
} as const;

/** Seat, and a plank on edge at each end holding it up. */
export function buildPenkki(): DecorSpec[] {
  const legTop = VALVOJA.seatTop - VALVOJA.slab;
  return [
    decor('valvojan-penkki-lankku', 'wood', VALVOJA.minX, VALVOJA.frontZ, VALVOJA.maxX, VALVOJA.wallZ, VALVOJA.slab, legTop),
    decor('valvojan-penkki-jalka-l', 'furniture', VALVOJA.minX + 0.08, VALVOJA.frontZ + 0.04, VALVOJA.minX + 0.14, VALVOJA.wallZ - 0.04, legTop),
    decor('valvojan-penkki-jalka-i', 'furniture', VALVOJA.maxX - 0.14, VALVOJA.frontZ + 0.04, VALVOJA.maxX - 0.08, VALVOJA.wallZ - 0.04, legTop),
  ];
}

/**
 * The man on it, facing the room rather than the wall he has his back to.
 *
 * `Koodinimi "Tyrde"` is on the front of his cap rather than on a sign of its
 * own, because that is where a LAN puts a name it wants read across a hall,
 * and because it is the only thing in the venue that says who somebody is at
 * the height you actually look at them.
 */
export const sitters: SitterSpec[] = [
  {
    name: 'valvoja',
    seat: [VALVOJA.x, VALVOJA.hipZ],
    seatHeight: VALVOJA.seatTop,
    height: VALVOJA.height,
    rotationY: Math.PI,
    cap: { text: 'Tyrde', color: '#f4f1e8', background: '#1d2b52' },
  },
];

/* -------------------------------------------------- juhlasalin orga ------ */

/**
 * ==========================================================================
 *  KOALA, JOKA OSOITTAA KIMMOLLE MISSÄ TÄMÄN KONE ON
 * ==========================================================================
 *  The second man awake in the hall, and the first one on his feet: an orga
 *  stood against the sali's end wall with his arm up, pointing at the table
 *  the run finishes on.
 *
 *  He is here because the last corner is the one place in the level where a
 *  runner has to be told something. Everything up to it is read off the
 *  building - a doorway, a corridor, a runway with bodies in it - but the
 *  finish is a chair among thirty identical chairs, and the only things
 *  saying which one is the yellow tabletop and the yellow monitor. A man
 *  pointing at it is the third, and it is the one that reads at a glance and
 *  from the far end of the aisle, which is all the time this corner gives you.
 *
 *  The lettering is who he is, the same way the valvoja's cap is. `Koala` is
 *  on his shirt because a handle goes on a shirt, and `Orga` is on the cap
 *  because at a LAN the cap says your *job* - the point of a lippalakki with
 *  ORGA on it is that it can be read by somebody who does not know you.
 *
 *  ------------------------------------------------------------------------
 *   WHERE HE STANDS, AND WHY THERE IS ONLY ONE PLACE HE CAN
 *  ------------------------------------------------------------------------
 *  "Against the far wall" sounds like a free choice and is not one. The far
 *  wall is the sali's east wall, and almost all of it is already spoken for:
 *
 *    - z -38.4 .. -34.0 is the valkokangas, and the projector throws at it
 *      from six metres back. A man standing anywhere along that stretch is
 *      a silhouette in the middle of somebody's clip.
 *    - z -34.55 .. -32.65 is `nukkuja-5`, asleep on the boards with his head
 *      to the wall. He is a collider and he was here first.
 *    - z -33.225 .. -30.0 is the two lanes the hall is run on and the four
 *      bodies between them, and the wall-side one has to stay runnable the
 *      whole way through - the rule the baari and the valvojan penkki are
 *      both placed under.
 *
 *  What is left is the corner north of the screen: the last metre of the east
 *  wall before the north wall, with the head of the aisle in front of it. It
 *  is the deepest point of the room, it is 0.55 m off the east wall, the
 *  north wall and the screen's north edge alike, and it is the one spot where
 *  a man can stand at the end of this hall without being in front of a
 *  picture, on top of a sleeper or in somebody's way.
 *
 *  It is also, as it happens, the best place for him: the aisle he is at the
 *  head of is the last leg of the route, so he is straight ahead for the
 *  whole of it - and he is far enough north of `finish.zone` that the run
 *  ends in front of him rather than through him.
 *
 *  Dressing, all of him: one `standers` entry and nothing else - no solid, no
 *  decor, no collider. So he is outside `simulationFingerprint()` and costs
 *  nobody a leaderboard time. See `src/config/CLAUDE.md`. `Level.test.ts`
 *  measures every clearance above rather than trusting this comment.
 * ==========================================================================
 */
const ORGA = {
  /**
   * The north-east corner, half a metre off both walls and half a metre north
   * of the valkokangas - the three clearances are the same number because the
   * corner is small enough that splitting it evenly is the whole decision.
   */
  x: LAN_EAST_WALL - 0.55,
  z: -38.95,
  height: 1.83,
} as const;

/**
 * What he is pointing at: Kimmo's own machine, taken from the box that *is* it
 * rather than typed out a second time. The goal-coloured monitor at the end of
 * the last table is the thing the whole run is aimed at, so it is the thing the
 * arm is aimed at too - and taking the height off the box is what keeps the arm
 * pointing *down* at a screen on a desk instead of over the top of it.
 */
const KIMMON_NAYTTO = boxCenter(
  COMPUTERS.decor.find((item) => item.name === `naytto-${KIMMO_TABLE_INDEX + 1}-i2`)!.box,
);
export const ORGA_TARGET: [number, number, number] = [
  KIMMON_NAYTTO.x,
  KIMMON_NAYTTO.y,
  KIMMON_NAYTTO.z,
];

/* ------------------------------------------------- saunan ovi ja Kempf --- */

/**
 * ==========================================================================
 *  THE DOOR IN THE FAR END WALL, AND THE MAN STANDING AT IT
 * ==========================================================================
 *  The venue's own floor plan gives it 72 m² of saunatilat, and they are
 *  behind the juhlasali's east wall - `sauna-vali` and `suihku-vali` are
 *  already back there dividing a footprint nobody can walk into. So the wall
 *  at the far end of the hall has a door in it, because a building with a
 *  sauna in it has a way into the sauna, and the only other thing on that
 *  wall is a projector screen.
 *
 *  It is locked, and it stays locked for the whole run. That is the entire
 *  content of it: the prompt promises the way every other door's does, the
 *  handle does nothing, and Kempf - who has been standing beside it since
 *  before anybody in this building woke up - explains. See `KEMPF` below and
 *  `KEMPF_LINE` in src/config/dialogue.ts for what he says.
 *
 *  ------------------------------------------------------------------------
 *   IT IS DRESSING, AND IT HAS TO BE
 *  ------------------------------------------------------------------------
 *  Every board here is `decor` and the man is a `standers` entry, so there is
 *  not one hashed number in the whole set-piece. `sali-ita` is solid for the
 *  length of the run whatever anybody presses: the leaf refuses nobody who
 *  was not already refused, and the press moves no collider. A door that
 *  opened would be a new way out of the room and would retire every
 *  leaderboard time - this one is a picture with a prompt on it, and
 *  `Level.test.ts` hashes the level with the whole thing lifted out to prove
 *  it cost nothing. See src/config/CLAUDE.md and src/world/Sauna.ts.
 *
 *  ------------------------------------------------------------------------
 *   WHERE ON THE WALL IT GOES
 *  ------------------------------------------------------------------------
 *  The valkokangas has that wall from z = -38.4 to -34.0, and `nukkuja-5` is
 *  asleep the length of himself along the next two metres of it. What is left
 *  is the 2.65 m between his feet and the south wall, and the leaf takes the
 *  northern end of that - which leaves the corner for the man, rather than
 *  putting him where the last stride of the hall comes through.
 * ==========================================================================
 */
export const SAUNA = {
  /** The inside face of `sali-ita`, which is the wall the leaf is hung on. */
  wallX: LAN_EAST_WALL,
  /**
   * Where along it. The north jamb clears `nukkuja-5`'s feet (`z = -32.65`)
   * by 0.18 m, and the south jamb leaves 1.43 m of wall - which is a place to
   * stand rather than a gap somebody is wedged into.
   */
  centerZ: -31.95,
  /** An interior door: the same 2.05 m leaf every other one indoors here is. */
  width: 0.9,
  height: 2.05,
  /** The word over it, clear of the karmi's head by a hand's width. */
  signY: 2.45,
  signHeight: 0.3,
} as const;

/**
 * The leaf, its karmi, its painike and the word over the top of it.
 *
 * Built exactly the way the keittiö's door is, because it is the same thing:
 * a door hung on a wall that stays solid. Proud of the wall by the thickness
 * of its own frame, with the karmi standing a little further out again, and
 * no two faces on one plane - coplanar decor z-fights, and a flickering door
 * at the end of a sixteen-metre hall is a thing you can see from the doorway.
 *
 * Hinged on the north jamb, which puts the painike on the south end of the
 * leaf - the end the man is standing at. A handle on the far side of a door
 * from whoever is guarding it is a handle nobody's eye goes to.
 *
 * The sign is as wide as the leaf and no wider. A door with a word over it is
 * a room with a name; a word wider than the door it names is a notice about
 * the wall.
 */
function saunanOvi(): {
  decor: DecorSpec[];
  handles: HandleSpec[];
  sign: SignSpec;
  /** The leaf's own box, which is also what F reaches for. */
  box: Box;
} {
  const face = SAUNA.wallX;
  const half = SAUNA.width / 2;
  const north = SAUNA.centerZ - half;
  const south = SAUNA.centerZ + half;
  const leaf = decor('saunan-ovi', 'door', face - 0.05, north, face - 0.01, south, SAUNA.height);
  return {
    decor: [
      leaf,
      decor('saunan-karmi-pohjoinen', 'furniture', face - 0.09, north - 0.07, face - 0.01, north, SAUNA.height + 0.08),
      decor('saunan-karmi-etela', 'furniture', face - 0.09, south, face - 0.01, south + 0.07, SAUNA.height + 0.08),
      decor('saunan-karmi-yla', 'furniture', face - 0.09, north - 0.07, face - 0.01, south + 0.07, 0.08, SAUNA.height),
    ],
    handles: [handle('saunan-painike', leaf.box, -1, -1)],
    sign: {
      text: 'SAUNA',
      // Two centimetres off the wall, which is in front of the plaster and
      // behind nothing: the karmi's head stops 30 cm below this.
      position: { x: face - 0.02, y: SAUNA.signY, z: SAUNA.centerZ },
      // Facing -X, i.e. back down the hall at whoever is reading it.
      rotationY: -Math.PI / 2,
      width: SAUNA.width,
      height: SAUNA.signHeight,
    },
    box: leaf.box,
  };
}

export const SAUNAN_OVI = saunanOvi();

/**
 * What F reaches for, which is the leaf itself.
 *
 * The box is taken off the board that draws it rather than typed a second
 * time, for the same reason the sima's glass is taken off the tuoppi that *is*
 * it: the thing you can see and the thing a hand goes to have to be one
 * object, or they drift apart by a centimetre and the prompt starts appearing
 * beside the door.
 */
export const SAUNA_SPEC: SaunaSpec = {
  id: 'saunan-ovi',
  // Promises exactly what the other three doors promise, and that is the
  // point of it: a prompt that said LUKOSSA would be the door telling you the
  // joke before the man does.
  label: 'AVAA SAUNAN OVI',
  box: SAUNAN_OVI.box,
  // And the man who answers it, by name. He is a `standers` entry a few lines
  // down and this is the only thing tying the two together - see `KEMPF`, and
  // `World.renderGreeting` for the hand he puts up when the handle goes.
  greeter: 'kempf',
};

/**
 * The man standing beside it.
 *
 * On the door's right as you face it, which is also the only side of it the
 * room has left: north of the leaf is a sleeping man and then the
 * valkokangas, and south of it is a metre and a half of wall and then the
 * corner - and the valvoja's bench stops 0.65 m short of that corner, so
 * there is a place to stand there and nothing else in it.
 *
 * He stands just outside the latch jamb, turned diagonally across the opening.
 * His shoulder overlaps the leaf from the hall and his raised right arm
 * reaches across it. The target stays in front of the wall so he still faces
 * partly into the room, with enough space behind him for the arm and torso.
 *
 * Dressing, all of him, like the orga at the other end of this same wall: one
 * `standers` entry and nothing else - no solid, no decor, no collider. So he
 * is outside `simulationFingerprint()` and costs nobody a leaderboard time.
 * `Level.test.ts` measures his clearance from every line the reference bot
 * actually runs rather than trusting this comment.
 */
const KEMPF = {
  stand: [SAUNA.wallX - 0.55, SAUNA.centerZ + SAUNA.width / 2 + 0.1] as [number, number],
  facing: [SAUNA.wallX - 0.9, SAUNA.centerZ] as [number, number],
  height: 1.81,
} as const;

export const standers: StanderSpec[] = [
  {
    name: 'orga',
    stand: [ORGA.x, ORGA.z],
    height: ORGA.height,
    // Turned to the same point his arm is aimed at, so his shoulders and his
    // hand say one thing rather than two. Derived rather than typed: move the
    // table and he keeps pointing at it.
    rotationY: Math.atan2(ORGA_TARGET[0] - ORGA.x, ORGA_TARGET[2] - ORGA.z),
    points: ORGA_TARGET,
    cap: { text: 'Orga', color: '#141a24', background: '#e3b23c' },
    shirt: { text: 'Koala', color: '#f4f1e8', background: '#2d6b52' },
  },
  // And the man in the tent, halfway down the yard, with the four people who
  // are still awake in front of him. See `DJ_SOLIDI` and `TELTAN_FANIT`.
  DJ_SOLIDI,
  ...TELTAN_FANIT,
  // And Kempf, at the other end of the same wall the orga is on, in front of
  // the one door in the venue that does not open. Last in the list because
  // every figure's face is rolled off its index: appending is free, and
  // inserting would re-cast everybody after it. See `KEMPF`.
  {
    name: 'kempf',
    stand: KEMPF.stand,
    height: KEMPF.height,
    // Across the opening, so the raised arm bars the doorway visually.
    rotationY: Math.atan2(KEMPF.facing[0] - KEMPF.stand[0], KEMPF.facing[1] - KEMPF.stand[1]),
    // Red, and printed with the reason nobody has asked him to move.
    cap: { text: 'PERSU', color: '#f4f1e8', background: '#c1272d' },
    // And his own name under it, which is what a shirt at a LAN is for.
    shirt: { text: 'Kempf', color: '#1d2b52', background: '#e8e4d9' },
  },
];
